
import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

import Navbar from "../components/Navbar";
import Footer from "../components/Footer";

// =====================================
// FRONTSPACE SERVICES AND PRODUCTS
// =====================================

const services = [
  {
    id: 1,
    title: "APT Containers",
    video: "/product_videos/aptcontainers.mp4",
    description:
      "APT Containers is a smart container tracking platform that helps businesses monitor shipments worldwide through an interactive global map. Users can track up to 100 saved containers, receive real-time shipment updates and notifications via WhatsApp or email, and manage their logistics in one place. Start with a 30-day free trial for greater visibility and control over your shipments.",
    website: "https://aptcontainers.com/",
  },
  {
    id: 2,
    title: "MindQuest",
    video: "/product_videos/mindquest.mp4",
    description:
      "MindQuest is an adaptive cognitive assessment platform that helps schools identify students' strengths and learning potential through age-appropriate reasoning and IQ tests. It provides insights into five areas of cognitive ability, helping educators recognize exceptional talent and identify students who may benefit from additional support.",
    website: "https://www.mindquestiq.com/",
  },
  {
    id: 3,
    title: "Global Intelligence & Information System (GIIS)",
    video: "/product_videos/globalsystem.mp4",
    description:
      "GIIS is a customizable intelligence and information platform that collects, centralizes, monitors, and analyzes publicly available information from sources worldwide. It enables users to track countries, industries, topics, and events in one place, providing structured analysis, reports, and interactive geographic maps to support informed decision-making.",
  },
  {
    id: 4,
    title: "Business Automation Solutions",
    video: "/product_videos/automatisation.mp4",
    description:
      "Business Automation Solutions by FrontSpace help businesses automate their digital operations through intelligent, connected workflows. Our solutions include AI-powered article creation and automatic publishing, social media content generation and posting, continuous website news updates, WhatsApp notifications, and digital business cards. By bringing these capabilities together, businesses can reduce manual work, maintain an active online presence, improve communication, and save valuable time.",
  },
  {
    id: 5,
    title: "FrontPen",
    video: "/product_videos/frontpen.mp4",
    description:
      "FrontPen is an AI-powered penetration testing service that helps businesses identify security weaknesses in websites, applications, APIs, and infrastructure. It validates vulnerabilities with evidence and provides clear reports with practical guidance, helping teams prioritize fixes and strengthen their security.",
  },
  {
    id: 6,
    title: "NGO Management System",
    video: "/product_videos/NGO Management System.mp4",
    description:
      "NGO Management System is a platform that helps non-governmental organizations manage projects, donors, volunteers, and funds in one place. It simplifies daily operations, tracks activities and budgets, and streamlines reporting, helping teams stay organized, improve transparency, and focus on creating a positive impact in their communities.",
  },
  {
    id: 7,
    title: "Security Operations Center",
    video: "/product_videos/soc.mp4",
    description:
      "Our SOC service provides 24/7 security monitoring, threat detection, and incident response without the need to build an in-house security operations center. We help organizations investigate suspicious activity, respond to threats, and continuously strengthen their defenses through managed cybersecurity services tailored to their needs.",
    website: "https://www.trustnet.co.il/",
  },
  {
    id: 8,
    title: "Cybersecurity Training",
    video: "/product_videos/cyber security training.mp4",
    description:
      "Our cybersecurity training combines the TechDX Bootcamp with TDX Arena's live training environment to help individuals and teams build practical skills. Through structured courses, hands-on labs, and realistic cyber scenarios, learners develop expertise in threat detection, ethical hacking, and incident response, preparing them for real-world cybersecurity challenges.",
    website: "https://techdx.ai/",
  },
];

// =====================================
// REUSABLE SERVICE CARD
// =====================================

function ServiceCard({ service }) {
  const videoRef = useRef(null);
  const [hasStarted, setHasStarted] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);

  // Automatically play when visible and pause when hidden
  useEffect(() => {
    const video = videoRef.current;

    if (!video || !service.video) return;

    const observer = new IntersectionObserver(
      async ([entry]) => {
        if (entry.isIntersecting) {
          try {
            video.muted = true;
            await video.play();
          } catch (error) {
            // Some browsers block autoplay.
            console.log("Video autoplay unavailable:", error);
          }
        } else {
          video.pause();
        }
      },
      {
        threshold: 0.5,
      }
    );

    observer.observe(video);

    return () => {
      observer.disconnect();
      video.pause();
    };
  }, [service.video]);

  return (
    <article
      className="
        bg-[#0A1024]
        border border-[#00F0FF]/20
        rounded-2xl
        overflow-hidden
        shadow-xl
        flex flex-col
        h-full
        hover:border-[#00F0FF]/50
        transition-all duration-300
      "
    >
      {/* =====================================
          VIDEO SECTION
      ===================================== */}

      <div className="relative w-full aspect-video bg-[#050814] overflow-hidden group">
        {service.video ? (
          <>
            <video
              ref={videoRef}
              muted
              playsInline
              loop
              controls
              preload="metadata"
              onPlay={() => {
                setIsPlaying(true);
                setHasStarted(true);
              }}
              onPause={() => {
                setIsPlaying(false);
              }}
              className="absolute inset-0 w-full h-full object-contain"
              aria-label={`${service.title} demonstration video`}
            >
              <source
                src={encodeURI(service.video)}
                type="video/mp4"
              />
              Your browser does not support this video.
            </video>

            {/* COVER SCREEN BEFORE PLAYBACK */}
            {!hasStarted && (
              <div
                className="
                  absolute inset-0 z-10
                  pointer-events-none
                  bg-gradient-to-br
                  from-[#0A1024]
                  via-[#102451]
                  to-[#050814]
                  flex flex-col
                  items-center
                  justify-center
                  p-6
                "
              >
                {/* Decorative glow */}
                <div
                  className="
                    absolute
                    w-48 h-48
                    rounded-full
                    bg-[#00F0FF]/10
                    blur-3xl
                  "
                />

                {/* Play icon */}
                <div
                  className="
                    relative
                    flex items-center justify-center
                    w-16 h-16
                    rounded-full
                    border border-[#00F0FF]/50
                    bg-[#00F0FF]/10
                    mb-5
                    shadow-[0_0_35px_rgba(0,240,255,0.15)]
                  "
                >
                  <svg
                    viewBox="0 0 24 24"
                    className="w-7 h-7 fill-[#00F0FF] ml-1"
                    aria-hidden="true"
                  >
                    <path d="M8 5v14l11-7z" />
                  </svg>
                </div>

                {/* Cover title */}
                <h3
                  className="
                    relative
                    text-white
                    text-lg sm:text-xl
                    font-bold
                    text-center
                    max-w-sm
                  "
                >
                  {service.title}
                </h3>

                {/* Cover subtitle */}
                <p
                  className="
                    relative
                    text-[#00F0FF]
                    text-xs
                    uppercase
                    tracking-[0.2em]
                    mt-3
                    text-center
                  "
                >
                  FrontSpace Digital Solutions
                </p>
              </div>
            )}

            {/* Playing indicator */}
            {isPlaying && (
              <div
                className="
                  absolute top-3 right-3
                  pointer-events-none
                  bg-black/60
                  text-[#00F0FF]
                  text-xs
                  px-3 py-1
                  rounded-full
                  opacity-0
                  group-hover:opacity-100
                  transition-opacity
                "
              >
                Playing
              </div>
            )}
          </>
        ) : (
          /* Fallback for services without a video */
          <div
            className="
              absolute inset-0
              bg-gradient-to-br
              from-[#0A1024]
              via-[#102451]
              to-[#050814]
              flex flex-col
              items-center justify-center
              p-6
            "
          >
            <div className="w-16 h-16 rounded-full bg-[#00F0FF]/10 border border-[#00F0FF]/30 flex items-center justify-center mb-5">
              <svg
                viewBox="0 0 24 24"
                className="w-8 h-8 text-[#00F0FF]"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                aria-hidden="true"
              >
                <rect x="3" y="3" width="18" height="18" rx="3" />
                <path d="M8 12h8M12 8v8" />
              </svg>
            </div>

            <h3 className="text-xl text-white font-bold text-center">
              {service.title}
            </h3>

            <p className="text-[#00F0FF] text-xs uppercase tracking-widest mt-3">
              FrontSpace Digital Solutions
            </p>
          </div>
        )}
      </div>

      {/* =====================================
          SERVICE INFORMATION
      ===================================== */}

      <div className="p-6 md:p-8 flex flex-col flex-1">
        <h2
          className="text-xl sm:text-2xl font-bold text-white mb-4"
          style={{ fontFamily: "Outfit, sans-serif" }}
        >
          {service.title}
        </h2>

        <p className="text-slate-400 leading-relaxed mb-6 flex-1">
          {service.description}
        </p>

        {/* Website button */}
        {service.website && (
          <div className="mt-auto pt-2">
            <a
              href={service.website}
              target="_blank"
              rel="noopener noreferrer"
              className="
                inline-flex
                items-center justify-center
                bg-[#00F0FF]
                text-[#050814]
                font-semibold
                px-6 py-3
                rounded-lg
                hover:bg-white
                transition-colors
              "
            >
              Visit Website ↗
            </a>
          </div>
        )}
      </div>
    </article>
  );
}

// =====================================
// SERVICES PAGE
// =====================================

export default function Services() {
  useEffect(() => {
    document.title =
      "Our Services & Digital Products | FrontSpace";

    const description =
      "Explore FrontSpace products and services including AI solutions, cybersecurity training, managed SOC, penetration testing, business automation, intelligence platforms, and custom software.";

    let meta = document.querySelector(
      'meta[name="description"]'
    );

    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "description");
      document.head.appendChild(meta);
    }

    meta.setAttribute("content", description);
  }, []);

  return (
    <div className="min-h-screen bg-[#050814] text-white overflow-x-hidden">
      {/* NAVBAR */}
      <Navbar />

      <main className="max-w-7xl mx-auto px-5 pt-32 pb-24">
        {/* =====================================
            PAGE HEADING
        ===================================== */}

        <div className="text-center mb-14">
          <h1
            className="text-3xl sm:text-4xl md:text-5xl font-bold mb-5"
            style={{ fontFamily: "Outfit, sans-serif" }}
          >
            Our{" "}
            <span className="text-[#00F0FF]">
              Services
            </span>
          </h1>

          <p
            className="text-slate-400 max-w-3xl mx-auto text-base sm:text-lg leading-relaxed"
            style={{ textWrap: "balance" }}
          >
            Explore the innovative digital products,
            intelligent platforms, and business automation
            solutions developed by FrontSpace.
          </p>
        </div>

        {/* =====================================
            ALL SERVICES - EQUAL SIZE CARDS
        ===================================== */}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-stretch">
          {services.map((service) => (
            <ServiceCard
              key={service.id}
              service={service}
            />
          ))}
        </div>

        {/* =====================================
            BOTTOM CALL TO ACTION
        ===================================== */}

        <section className="text-center mt-20">
          <h2
            className="text-2xl sm:text-3xl md:text-4xl font-bold mb-5"
            style={{ fontFamily: "Outfit, sans-serif" }}
          >
            Looking for a{" "}
            <span className="text-[#00F0FF]">
              Custom Solution?
            </span>
          </h2>

          <p
            className="text-slate-400 max-w-xl mx-auto mb-8 leading-relaxed"
            style={{ textWrap: "balance" }}
          >
            From AI-powered automation to custom digital
            platforms, FrontSpace develops technology
            solutions tailored to your business needs.
          </p>

          <Link
            to="/contact"
            className="
              inline-flex
              items-center justify-center
              bg-[#00F0FF]
              text-[#050814]
              font-semibold
              px-8 py-4
              rounded-lg
              hover:bg-white
              transition-colors
            "
          >
            Contact Us
          </Link>
        </section>
      </main>

      {/* FOOTER */}
      <Footer />
    </div>
  );
}
